import React from "react";
import { Box, Grid, Spinner } from "theme-ui";

import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import { themevals } from "theme/themevals";
import { mnt, mntStatCard } from "theme/maintenanceTokens";

/**
 * MASTER_PLAN.md §3.9 #22 — the four-card stat row on Plan Detail
 * (mockup 13 top of main column). Contract with the caller: pass in
 * either `stats` or `loading` — `null`/undefined stats with
 * loading=false renders a soft placeholder rather than "0".
 *
 * SESSION 46 density pass (ALIGNMENT_REVIEW Misalignment 4). The
 * mockup's cards are 66px tall: label, value, sub-line packed tight,
 * four across. Shipped cards had grown to ~2x that height with the
 * value floating in whitespace. Compacted: smaller padding, no
 * minHeight, tighter line stacking, 4-across from the tablet
 * breakpoint up. Borders dropped — the mockup cards are flat #F1EFE8
 * blocks, and the border+fill combination read heavier than anything
 * on the reference.
 *
 * Committed count comes off the plan itself (not stats) because it's
 * a config field, not an aggregate. If the plan is ongoing (no
 * committedSiteCount), the Sites card omits the "N committed" line.
 *
 * "Completed" copy is Phase-1-honest: it's the count of plan sites
 * that have any service_event, not "completed against committed" —
 * cadence-driven completion is Phase 2 (open decision #7).
 */
export type PlanStats = {
  sitesInPlan: number;
  sitesServiced: number;
  openWos: number;
  openWosNeedMatch: number;
  openWosNeedConvert: number;
  yardsYtd: number;
};

export default function PlanStatCards({
  stats,
  committedSiteCount,
  endDate,
  targetDate,
  termStart,
  loading,
}: {
  stats: PlanStats | null;
  committedSiteCount: number | null | undefined;
  /** SESSION 45 GAP-31 — term-plan endDate for the pace projection
   * strip. Ongoing plans (endDate = null) skip the strip. */
  endDate?: string | null;
  /** P-d — the deadline that actually governs the commitment. Usually
   * the plan's endDate, but an ONGOING plan can still carry a hard
   * commitment through a batch target (EMCOR: open-ended plan, 125
   * locations due Oct 31). The caller resolves which date rules;
   * without this, an ongoing-but-committed plan showed no pace at all,
   * which is exactly the plan shape that most needs it. */
  targetDate?: string | null;
  /** When the commitment started running, for the actual-rate half of
   * the pace read. Plan createdAt is the honest proxy: there is no
   * separate term-start column. */
  termStart?: string | null;
  loading?: boolean;
}) {
  const cards: Array<{
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
    valueColor?: string;
  }> = [
    {
      label: "Locations in plan",
      value: stats?.sitesInPlan ?? 0,
      sub: committedSiteCount != null ? `${committedSiteCount} committed` : "Ongoing",
    },
    {
      label: "Completed",
      value: stats?.sitesServiced ?? 0,
      valueColor: (stats?.sitesServiced ?? 0) > 0 ? "#27500A" : undefined,
      // GAP-3 — mockup 13 reads `18 of 200 · 9%` against the committed
      // count. Show serviced-of-committed with the percentage when a
      // commitment exists; fall back to "All-time" for ongoing plans
      // with no committed target.
      sub:
        committedSiteCount != null && committedSiteCount > 0
          ? `${stats?.sitesServiced ?? 0} of ${committedSiteCount} \u00b7 ${Math.round(((stats?.sitesServiced ?? 0) / committedSiteCount) * 100)}%`
          : "All-time",
    },
    {
      label: "Open work orders",
      value: stats?.openWos ?? 0,
      valueColor: (stats?.openWos ?? 0) > 0 ? "#633806" : undefined,
      // GAP-2 — split the open count into what's actually needed next
      // (`N need match · M need convert`) instead of a generic waiting
      // line, matching the mockup's `3 in review · 4 quoted` intent.
      // Falls back gracefully if the split fields aren't present (older
      // backend) by showing the generic copy.
      sub: openWoSubline(stats),
    },
    {
      label: "Yards YTD",
      value: formatYards(stats?.yardsYtd ?? 0),
      sub:
        (stats?.sitesServiced ?? 0) > 0 && (stats?.yardsYtd ?? 0) > 0
          ? `avg ${((stats?.yardsYtd ?? 0) / Math.max(stats?.sitesServiced ?? 1, 1)).toFixed(1)} / site`
          : "\u00A0",
    },
  ];

  // SESSION 45 GAP-31 / P-d — pace projection. Two numbers, not one:
  // the rate needed from here to hit the commitment, and the rate
  // actually being run so far. "4.2 a week needed" means nothing until
  // you know you are running 1.6.
  //
  // Governed by `targetDate ?? endDate`, so an ongoing plan with a
  // batch commitment gets a pace read too (P-d). Nothing to project
  // against without a commitment and a date, so it stays hidden there.
  const pace = React.useMemo(() => {
    const governing = targetDate ?? endDate ?? null;
    if (!governing || committedSiteCount == null || !stats) return null;
    const completed = stats.sitesServiced;
    const remaining = Math.max(committedSiteCount - completed, 0);
    if (remaining === 0) {
      return { onTrack: true, weeksLeft: 0, needed: 0, actual: null, remaining: 0 };
    }

    const now = new Date();
    const end = new Date(governing);
    const msLeft = end.getTime() - now.getTime();
    const weeksLeft = msLeft > 0 ? msLeft / (1000 * 60 * 60 * 24 * 7) : 0;

    // Actual rate so far, when we know when the clock started.
    let actual: number | null = null;
    if (termStart) {
      const weeksElapsed =
        (now.getTime() - new Date(termStart).getTime()) / (1000 * 60 * 60 * 24 * 7);
      if (weeksElapsed >= 1) actual = completed / weeksElapsed;
    }

    if (weeksLeft <= 0) {
      // Deadline passed — the caller renders the closing warning
      // separately; the strip just states the shortfall.
      return { onTrack: false, weeksLeft: 0, needed: Infinity, actual, remaining };
    }
    const needed = remaining / weeksLeft;
    return {
      onTrack: actual != null ? actual >= needed : null,
      weeksLeft,
      needed,
      actual,
      remaining,
    };
  }, [endDate, targetDate, termStart, committedSiteCount, stats]);

  return (
    <Box sx={{ marginBottom: "20px" }}>
      <Grid columns={[2, 4]} gap={"12px"} sx={{ marginBottom: pace ? "10px" : 0 }}>
        {cards.map((c) => (
          <Box
            key={c.label}
            sx={{
              // mntStatCard = mockup 13's borderless beige card. Kept the
              // Session 46 compact padding (12/14) which is tighter than
              // the index cards by design (four across a narrower column).
              ...mntStatCard(),
              padding: "12px 14px",
            }}
          >
            <Subtext
              fontSize={"10.5px"}
              fontFamily={"Poppins"}
              fontWeight={600}
              margin={0}
              color={themevals.colors.dkgrey}
              sx={{ letterSpacing: "0.02em" }}
            >
              {c.label.toUpperCase()}
            </Subtext>
            {loading && !stats ? (
              <Box sx={{ marginTop: "4px" }}>
                <Spinner size={16} />
              </Box>
            ) : (
              <H5
                fontFamily="Poppins"
                fontWeight={500}
                fontSize={"20px"}
                margin={0}
                marginTop={"4px"}
                sx={{ color: c.valueColor, lineHeight: 1.2 }}
              >
                {c.value}
              </H5>
            )}
            <Subtext
              fontSize={"11px"}
              fontFamily={"Poppins"}
              margin={0}
              marginTop={"2px"}
              color={themevals.colors.dkgrey}
            >
              {c.sub}
            </Subtext>
          </Box>
        ))}
      </Grid>
      {pace && (
        <Box
          sx={{
            background: pace.onTrack === true ? mnt.greenPill : mnt.amberPill,
            border:
              "1px solid " + (pace.onTrack === true ? mnt.green : mnt.amberStroke),
            borderRadius: "8px",
            padding: "8px 14px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <Subtext
            fontSize={"10.5px"}
            fontFamily={"Poppins"}
            fontWeight={600}
            margin={0}
            color={pace.onTrack === true ? mnt.green : mnt.amber}
          >
            {pace.onTrack === true ? "ON PACE" : pace.onTrack === false ? "BEHIND" : "PACE"}
          </Subtext>
          <Subtext
            fontSize={"12px"}
            fontFamily={"Poppins"}
            margin={0}
            color={pace.onTrack === true ? mnt.green : mnt.amber}
          >
            {paceLine(pace)}
          </Subtext>
        </Box>
      )}
    </Box>
  );
}

function paceLine(pace: {
  onTrack: boolean | null;
  weeksLeft: number;
  needed: number;
  actual: number | null;
  remaining: number;
}): string {
  if (pace.remaining === 0) return "Committed count met.";
  if (pace.weeksLeft <= 0) {
    return (
      pace.remaining +
      (pace.remaining === 1 ? " location" : " locations") +
      " still to service; the target date has passed."
    );
  }
  const parts = [
    pace.needed.toFixed(1) + " / week needed",
    pace.actual != null ? "running " + pace.actual.toFixed(1) + " / week" : null,
    pace.remaining + " remaining",
    pace.weeksLeft.toFixed(0) + " weeks left",
  ].filter(Boolean) as string[];
  return parts.join(" \u00b7 ");
}

function openWoSubline(stats: PlanStats | null): string {
  const open = stats?.openWos ?? 0;
  if (open === 0) return "Nothing waiting";
  const needMatch = stats?.openWosNeedMatch ?? 0;
  const needConvert = stats?.openWosNeedConvert ?? 0;
  // Only render the split when it actually accounts for the open count
  // (guards against an older backend that doesn't return the split, in
  // which case both are 0 and we fall back to the generic line).
  if (needMatch + needConvert === 0) return "Waiting on match or RFQ";
  const parts: string[] = [];
  if (needMatch > 0) parts.push(`${needMatch} need match`);
  if (needConvert > 0) parts.push(`${needConvert} need RFQ`);
  return parts.join(" \u00b7 ");
}

function formatYards(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  if (v % 1 === 0) return String(v);
  return v.toFixed(1);
}
